import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  View,
  StyleSheet,
  Text,
  TouchableOpacity,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { usePantryName } from '../pantry/usePantryName';
import { substituteName } from '../../utils/substituteText';
import {
  Paprika,
  PaprikaSpacing,
  PaprikaTypography,
  PaprikaRadius,
  PaprikaFreshness,
} from '../../styles/theme';
import { useLufaDealsEnabled } from '../../hooks/useLufaDealsEnabled';
import { SubstitutionSheet, ChosenSubstitute } from './SubstitutionSheet';
import { ServingsStepper } from './ServingsStepper';
import { StockMark } from './StockMark';
import { findSubstitutions } from '../../services/ingredientSubstitutionService';
import { groupIngredientsForDisplay } from './ingredientGrouping';
import {
  deriveIngredientStatuses,
  statusAfterRemoving,
  type IngredientInventoryMatch,
  type IngredientPantryStatus,
} from '../../services/ingredientStatusService';
import type { LearnedCoverage, CoverageDetail } from '../../services/ingredientCoverageService';
import type { InventoryItem } from '../../redux/inventorySlice';
import { parseIngredientText } from '../../utils/ingredientParser';
import { isInlineIngredientHeader } from '../../utils/inlineIngredientHeader';
import { adjustQuantity } from '../../utils/adjustRecipeQuantities';
import type { RecipeAdjustment } from '../../hooks/useRecipeAdjustment';
import { AdjustedChip } from '../shared/AdjustedChip';
import { ingredientMatchText } from '../../services/inventoryMatchService';
import { resolveIngredient } from '../../utils/resolveIngredient';

export interface RecipeIngredient {
  id: number;
  name: string;
  original: string;
  amount: number;
  unit: string;
  image?: string;
}

export interface RecipeIngredientGroup {
  name: string;
  items: string[];
}

interface RecipeIngredientsProps {
  ingredients: RecipeIngredient[];
  groups?: RecipeIngredientGroup[];
  servings: number;
  baseServings: number;
  onServingsChange?: (delta: number) => void;
  yieldLabel?: string;
  onAddIngredient?: (ingredient: RecipeIngredient) => void;
  onAddToGrocery?: (items: { name: string; original: string }[]) => void;
  // Pantry crossing: status is DERIVED from the inventory, never user-entered.
  inventory?: InventoryItem[];
  // Pantry corrections (the only way to "fix" a status)
  /** The toast names `match.inventoryItemName` — the row removed, not the recipe line (#805). */
  onRemoveFromPantry?: (match: IngredientInventoryMatch) => void;
  /** What the user has already answered about related-ingredient coverage. */
  learnedCoverage?: LearnedCoverage;
  /** Basiques the cook turned off — bought like anything else, never assumed. */
  excludedStaples?: readonly string[];
  /** "You have {have} — does that cover {need}?" answered. Ids, not display text. */
  onAnswerCoverage?: (pair: { have: string; need: string; covers: boolean }) => void;
  onAddToPantry?: (ingredientName: string) => void;
  onOpenPantry?: () => void;
  // Lufa deal matches - indices of ingredients that have deals
  lufaDealIndices?: Set<number>;
  /**
   * Servings ratio + measurement system, shared with the steps and cooking
   * mode so a quantity can never read differently on two screens.
   */
  adjustment: RecipeAdjustment;
  /** Restores the recipe's own servings, for the "rétablir" affordance. */
  onResetServings?: () => void;
}

const isWeb = Platform.OS === 'web';
const WIDE_BREAKPOINT = 768;

// Row actions (substitute, pantry-correct) are revealed on hover. On touch
// devices there is no hover, so they stay permanently visible instead — an
// invisible-but-tappable control is worse than a slightly busier row.
const canHover =
  isWeb && typeof window !== 'undefined' && !!window.matchMedia?.('(hover: hover)').matches;

interface IngredientRowProps {
  rowKey: number;
  status: IngredientPantryStatus;
  isHave: boolean;
  displayNum: string;
  displayUnit: string;
  remainder: string;
  appliedSubName?: string;
  hasSubs: boolean;
  isActiveSub: boolean;
  isPending: boolean;
  showLufa: boolean;
  twoCol: boolean;
  replaceLabel: string;
  /** Quantity differs from the recipe as written (rescaled or converted). */
  qtyChanged: boolean;
  /** The as-written quantity, shown on tap / web hover. */
  originalValue: string;
  originalLabel: string;
  isRevealed: boolean;
  onPressQty: () => void;
  onPressRow: () => void;
  onOpenSub: () => void;
  onToggleCorrection: () => void;
  correctionStrip: React.ReactNode;
}

/**
 * One ingredient row. The substitute chip and the pantry StockMark are damped
 * to near-transparent until the pointer is over the row: on a 23-ingredient
 * recipe, 19 identical cart icons and 16 identical "remplacer" labels carry no
 * information — the absence of a green check already means "to buy".
 *
 * Hover is tracked with NATIVE pointerenter/pointerleave on the row's DOM node
 * rather than Pressable's onHoverIn/Out. Every RNW Pressable runs useHover with
 * contain:true, so a child Pressable (the substitute chip, the StockMark)
 * dispatches a react-gui:hover:lock event that un-hovers the parent row exactly
 * as the cursor reaches the control — hiding it at the moment of the click. See
 * src/components/weekly/v2/desktop/SlotCard.tsx for the same fix.
 *
 * The controls stay mounted and interactive at all times and only their opacity
 * changes; conditionally mounting an element under the cursor churns hover state
 * and flickers.
 */
const IngredientRow: React.FC<IngredientRowProps> = ({
  rowKey,
  status,
  isHave,
  displayNum,
  displayUnit,
  remainder,
  appliedSubName,
  hasSubs,
  isActiveSub,
  isPending,
  showLufa,
  twoCol,
  replaceLabel,
  qtyChanged,
  originalValue,
  originalLabel,
  isRevealed,
  onPressQty,
  onPressRow,
  onOpenSub,
  onToggleCorrection,
  correctionStrip,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const rowRef = useRef<any>(null);

  useEffect(() => {
    if (!canHover) return;
    const el = rowRef.current as HTMLElement | null;
    if (!el) return;

    const handlePointerEnter = () => setIsHovered(true);
    const handlePointerLeave = () => setIsHovered(false);

    el.addEventListener('pointerenter', handlePointerEnter);
    el.addEventListener('pointerleave', handlePointerLeave);
    return () => {
      el.removeEventListener('pointerenter', handlePointerEnter);
      el.removeEventListener('pointerleave', handlePointerLeave);
    };
  }, []);

  // Pantry checks always read at full strength (they carry information). Carts
  // on buy-rows are damped until hover. Without hover support, nothing is damped.
  const revealed = !canHover || isHovered || isActiveSub || isPending;
  const markOpacity = isHave || revealed ? 1 : 0.35;

  // One right-aligned string, not two fixed-width columns. "1,5 kg", "2 boîtes",
  // or a bare "1" when the recipe gives a count with no unit.
  const qtyText = [displayNum, displayUnit].filter(Boolean).join(' ');

  return (
    // Status is in the testID because it is otherwise only expressed as a
    // style: browser QA has no other way to tell a pantry check from a cart.
    <View
      key={rowKey}
      testID={`ingredient-row-${status}`}
      style={twoCol ? styles.rowGrid : undefined}
    >
      <TouchableOpacity
        ref={rowRef}
        style={[
          styles.row,
          isActiveSub && styles.rowActiveSub,
          isPending && styles.rowPending,
        ]}
        activeOpacity={hasSubs ? 0.7 : 1}
        onPress={onPressRow}
      >
        <View style={{ opacity: markOpacity }}>
          <StockMark status={status} onPress={onToggleCorrection} />
        </View>

        <View style={styles.nameBlock}>
          <Text style={[styles.name, isHave ? styles.nameHave : styles.nameBuy]} numberOfLines={3}>
            {remainder}
          </Text>
          {isRevealed && (
            <Text style={styles.originalText} numberOfLines={1}>
              {originalLabel}
            </Text>
          )}
          {!!appliedSubName && (
            <Text style={styles.appliedSubText} numberOfLines={1}>
              → {appliedSubName}
            </Text>
          )}
        </View>

        {/* Icon only. The word "remplacer" used to sit at the far right, which
            is now the quantity's column — and the label was redundant with the
            icon beside it. */}
        {hasSubs && (
          <TouchableOpacity
            style={[styles.replaceButton, { opacity: revealed ? 1 : 0 }]}
            onPress={onOpenSub}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            activeOpacity={0.6}
            accessibilityRole="button"
            // The name stays in the a11y label — the icon lost its visible text,
            // so without it every substitute button on the screen announces
            // identically and a screen reader cannot tell them apart.
            accessibilityLabel={`${replaceLabel} — ${remainder}`}
            {...(isWeb ? { title: replaceLabel } : {})}
          >
            <Feather
              name="refresh-cw"
              size={13}
              color={isActiveSub ? Paprika.accent : Paprika.textMuted}
            />
          </TouchableOpacity>
        )}

        {showLufa && (
          <View style={styles.lufaBadge}>
            <Text style={styles.lufaBadgeText}>Lufa</Text>
          </View>
        )}

        {qtyText ? (
          <TouchableOpacity
            style={styles.qtyBlock}
            onPress={onPressQty}
            activeOpacity={qtyChanged ? 0.6 : 1}
            // Web hover gives the original without a tap.
            {...(isWeb && qtyChanged ? { title: originalLabel } : {})}
          >
            <Text style={[styles.qtyText, qtyChanged && styles.qtyChanged]} numberOfLines={1}>
              {qtyText}
            </Text>
          </TouchableOpacity>
        ) : null}
      </TouchableOpacity>
      {/* Rule under each row, inset to start under the name. Suppressed in the
          two-column grid: the columns' rows have independent heights, so the
          rules land at different y-positions and the shorter column leaves one
          hanging with nothing opposite it — it reads as a broken table. */}
      {!twoCol && <View style={styles.rowDivider} />}
      {isPending && correctionStrip}
    </View>
  );
};

export const RecipeIngredients: React.FC<RecipeIngredientsProps> = ({
  ingredients,
  groups,
  servings,
  baseServings,
  onServingsChange,
  yieldLabel,
  onAddIngredient,
  onAddToGrocery,
  inventory = [],
  learnedCoverage,
  excludedStaples,
  onAnswerCoverage,
  onRemoveFromPantry,
  onAddToPantry,
  onOpenPantry,
  lufaDealIndices = new Set(),
  adjustment,
  onResetServings,
}) => {
  const { t, i18n } = useTranslation();
  const pantryName = usePantryName();
  const lufaEnabled = useLufaDealsEnabled();
  const { width } = useWindowDimensions();
  const twoCol = isWeb && width >= 900;
  // Layout is driven by width, not platform, so a narrow browser window renders
  // the phone layout the way the phone actually gets it.
  const isWide = width >= WIDE_BREAKPOINT;
  const gutter = isWide ? 56 : PaprikaSpacing.md;

  // Which row is showing its as-written value (tap the marked quantity).
  const [revealedOriginal, setRevealedOriginal] = useState<number | null>(null);

  // Substitution state
  const [activeSub, setActiveSub] = useState<string | null>(null);
  const [appliedSubs, setAppliedSubs] = useState<Record<string, ChosenSubstitute>>({});

  // Pantry correction strip (which ingredient id has it open)
  const [pendingCorrection, setPendingCorrection] = useState<number | null>(null);
  // The row whose strip is asking about the NEXT pantry row that covers it,
  // after « Je n'en ai plus » removed the first one (#805).
  const [outFollowUp, setOutFollowUp] = useState<number | null>(null);

  /**
   * Whether the whole ingredients section is folded away (#580).
   *
   * Deliberately local and NOT persisted: opening any recipe must always show
   * every ingredient. A remembered "collapsed" would mean one tap silently
   * hides the ingredients of every recipe from then on — which is the report
   * this fix answers, re-created by a different route.
   */
  const [isCollapsed, setIsCollapsed] = useState(false);
  const toggleCollapsed = () => setIsCollapsed(prev => !prev);

  // Derive have/buy/staple by crossing the recipe with the pantry
  const statusResult = useMemo(
    () => deriveIngredientStatuses(ingredients, inventory, learnedCoverage, excludedStaples),
    [ingredients, inventory, learnedCoverage, excludedStaples]
  );
  const statusById = useMemo(() => {
    const map = new Map<
      number,
      {
        status: IngredientPantryStatus;
        match: IngredientInventoryMatch | null;
        detail: CoverageDetail | null;
        askedAbout: { have: string; need: string } | null;
      }
    >();
    ingredients.forEach((ing, idx) => {
      map.set(ing.id, {
        status: statusResult.statuses[idx],
        match: statusResult.matches[idx],
        detail: statusResult.details[idx] ?? null,
        askedAbout: statusResult.askedAbout[idx] ?? null,
      });
    });
    return map;
  }, [ingredients, statusResult]);

  // A pantry row in the reader's language. The row, when it is still on the
  // shelf, because only it knows both names of a delivery's food.
  const shelfName = useCallback(
    (id: string | undefined, name: string | undefined) =>
      pantryName(inventory.find(item => item.id === id) ?? { name: name ?? '' }),
    [inventory, pantryName]
  );

  // Canonical ids the pantry holds, for the substitution sheet's "you own this"
  // ordering. Derived from the same index the coverage answers come from.
  const ownedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const item of inventory) {
      if (item.status && item.status !== 'active') continue;
      const id = resolveIngredient(item.name).canonicalId;
      if (id) ids.add(id);
    }
    return ids;
  }, [inventory]);

  const hasGroups = !!(groups && groups.length > 0 && groups.some(g => g.items && g.items.length > 0));

  // Section labels some publishers emit inline in `recipeIngredient`, which were
  // persisted as ingredients and rendered as buyable rows — "POUR LA CRÈME
  // FOUETTÉE", and "En accompagnement - Patates Deluxe" (#594).
  //
  // Takes the following line because that is what corroborates a marker: a real
  // section is followed by the ingredients under it, so "Sel et poivre :" stays
  // food. See `isInlineIngredientHeader` for the catalog audit behind the rule.
  const isInlineHeader = (ing: RecipeIngredient): boolean => {
    const idx = ingredients.indexOf(ing);
    const next = idx >= 0 ? ingredients[idx + 1] : undefined;
    return isInlineIngredientHeader(
      ing.original || ing.name || '',
      next ? next.original || next.name || '' : undefined,
    );
  };

  const hasInlineHeaders = !hasGroups && ingredients.some(isInlineHeader);

  // Recipe order is preserved verbatim. There used to be a sort here that pushed
  // common seasonings to the bottom of the list; it dragged "kosher salt" away
  // from FOR THE RICE, where the recipe actually uses it.
  const sortedIngredients = ingredients;

  const handleAddBuyToGrocery = () => {
    const buyItems = ingredients
      .filter(ing => statusById.get(ing.id)?.status === 'buy')
      .map(ing => ({
        name: parseIngredientText(ing.original || ing.name).remainder,
        original: ing.original || ing.name,
      }))
      .filter(item => (item.name && item.name.trim()) || (item.original && item.original.trim()));
    onAddToGrocery?.(buyItems);
  };

  const handleOpenSub = (ingredientName: string) => {
    setActiveSub(ingredientName);
  };

  const handleSelectSub = (sub: ChosenSubstitute) => {
    if (activeSub) {
      setAppliedSubs(prev => ({ ...prev, [activeSub]: sub }));
    }
    setActiveSub(null);
  };

  // Inline confirmation strip under a row — corrections go through the pantry
  const renderCorrectionStrip = (
    ingredient: RecipeIngredient,
    status: IngredientPantryStatus,
    match: IngredientInventoryMatch | null,
    displayName: string,
    detail?: CoverageDetail | null,
    askedAbout?: { have: string; need: string } | null,
  ) => {
    const close = () => {
      setPendingCorrection(null);
      setOutFollowUp(null);
    };

    // 'probably' is its own shape, not a variant of the correction question.
    // The other three tell the user something and offer one fix; this one asks
    // a question that only they can answer, so it gets two equal answers and
    // the "unsure" tint rather than the plain card.
    if (status === 'probably' && askedAbout?.have && askedAbout?.need) {
      const answer = (covers: boolean) => {
        onAnswerCoverage?.({ ...askedAbout, covers });
        close();
      };
      return (
        <View style={styles.coverageStrip} testID="coverage-confirm">
          {/*
            Name BOTH sides. "Vous avez Tomates en grappe. Assez ?" never said
            what it was asking about, which read as a non sequitur next to a
            line calling for tomato paste.
          */}
          <Text style={styles.coverageQuestion} numberOfLines={3}>
            {t('recipeDetail.coverageQuestion', {
              defaultValue: 'Utiliser « {{item}} » pour « {{need}} » ?',
              item: shelfName(match?.inventoryItemId, detail?.itemName ?? match?.inventoryItemName),
              need: displayName,
            })}
          </Text>
          {/*
            The arithmetic, when there is any, on its own line and phrased here
            rather than in the service — which does not know the language and
            was emitting an English "— need 2" into the French UI.
          */}
          {detail?.available != null && detail.unit ? (
            <Text style={styles.coverageYield} numberOfLines={2}>
              {detail.needed != null
                ? t('recipeDetail.coverageYieldNeeded', {
                    defaultValue: '{{owned}} ≈ {{available}} {{unit}} — il en faut {{needed}}',
                    owned: detail.itemQuantity
                      ? `${detail.itemQuantity} ${shelfName(match?.inventoryItemId, detail.itemName)}`
                      : shelfName(match?.inventoryItemId, detail.itemName),
                    available: detail.available,
                    unit: detail.unit,
                    needed: detail.needed,
                  })
                : t('recipeDetail.coverageYield', {
                    defaultValue: '{{owned}} ≈ {{available}} {{unit}}',
                    owned: detail.itemQuantity
                      ? `${detail.itemQuantity} ${shelfName(match?.inventoryItemId, detail.itemName)}`
                      : shelfName(match?.inventoryItemId, detail.itemName),
                    available: detail.available,
                    unit: detail.unit,
                  })}
            </Text>
          ) : null}
          <View style={styles.coverageActions}>
            <TouchableOpacity
              style={styles.coverageYes}
              onPress={() => answer(true)}
              activeOpacity={0.85}
              accessibilityRole="button"
            >
              <Text style={styles.coverageYesText}>
                {t('recipeDetail.coverageYes', "Oui, j'en ai assez")}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.coverageNo}
              onPress={() => answer(false)}
              activeOpacity={0.7}
              accessibilityRole="button"
            >
              <Text style={styles.coverageNoText}>
                {t('recipeDetail.coverageNo', 'Non, à acheter')}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      );
    }

    let question: string;
    let actionLabel: string;
    let actionColor = Paprika.accent;
    let cancelLabel = t('common.cancel', 'Annuler');
    let onAction: () => void;

    if (status === 'buy') {
      question = t('recipeDetail.correctionHaveQuestion', {
        defaultValue: 'Vous avez déjà « {{name}} » ?',
        name: displayName,
      });
      actionLabel = t('recipeDetail.correctionHaveAction', "Je l'ai — ranger au garde-manger");
      actionColor = Paprika.success;
      onAction = () => {
        // The line the MATCHER reduces, not the line the row displays (#601).
        //
        // This used to send `displayName` — the quantity-stripped remainder the
        // row renders. `cleanPantryName` is not indifferent to that difference:
        // on "1 pound spaghetti or linguine" it keeps "spaghetti", on the
        // remainder "spaghetti or linguine" it keeps "linguine". So the pantry
        // stored one alternative while the recipe kept asking about the other,
        // and the row never turned into a check no matter how often it was
        // tapped. Feeding both sides the same string makes them agree by
        // construction rather than by luck.
        onAddToPantry?.(ingredientMatchText(ingredient));
        close();
      };
    } else if (status === 'staple' || !match) {
      question = t('recipeDetail.correctionStapleQuestion', 'Basique présumé — plus en stock ?');
      actionLabel = t('recipeDetail.correctionStapleAction', "Ajouter à l'épicerie");
      onAction = () => {
        onAddToGrocery?.([
          {
            name: displayName,
            original: ingredient.original || ingredient.name,
          },
        ]);
        close();
      };
    } else {
      // Name the pantry row, not the recipe line (#805). Several rows can cover
      // one line — Tomates, Cocktail tomatoes — and the row only ever names the
      // first. Asking « Plus de « tomates moyennes » ? » and removing one of them
      // left the check in place with no way to see why.
      const isFollowUp = outFollowUp === ingredient.id;
      question = isFollowUp
        ? t('recipeDetail.correctionOutNextQuestion', {
            defaultValue: 'Il reste « {{item}} ». Fini aussi ?',
            item: shelfName(match.inventoryItemId, match.inventoryItemName),
          })
        : t('recipeDetail.correctionOutQuestion', {
            defaultValue: 'Au garde-manger : « {{item}} »',
            item: shelfName(match.inventoryItemId, match.inventoryItemName),
          });
      actionLabel = isFollowUp
        ? t('recipeDetail.correctionOutNextAction', 'Oui, fini')
        : t('recipeDetail.correctionOutAction', "Je n'en ai plus");
      if (isFollowUp) cancelLabel = t('recipeDetail.correctionOutNextKeep', "Non, j'en ai");
      onAction = () => {
        // Asked before the dispatch: the row's answer once this pantry row is gone.
        const next = statusAfterRemoving(
          ingredient,
          inventory,
          match.inventoryItemId,
          learnedCoverage,
          excludedStaples
        );
        onRemoveFromPantry?.(match);
        if (next.status === 'buy') {
          close();
        } else {
          // Still covered: keep the strip open. Another row of the same food
          // gets the follow-up question; a 'probably' or 'staple' answer gets its
          // own strip, which is the next thing the cook would have to settle.
          setOutFollowUp(next.status === 'have' && next.match ? ingredient.id : null);
        }
      };
    }

    return (
      <View style={styles.correctionStrip} testID="correction-strip">
        <Text style={styles.correctionQuestion} numberOfLines={2}>
          {question}
        </Text>
        <TouchableOpacity
          style={[styles.correctionAction, { backgroundColor: actionColor }]}
          onPress={onAction}
          activeOpacity={0.85}
          accessibilityRole="button"
          testID="correction-action"
        >
          <Text style={styles.correctionActionText}>{actionLabel}</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.correctionCancel}
          onPress={close}
          activeOpacity={0.7}
          accessibilityRole="button"
          testID="correction-cancel"
        >
          <Text style={styles.correctionCancelText}>{cancelLabel}</Text>
        </TouchableOpacity>
      </View>
    );
  };

  // Quantity/name for display: parsed, scaled to the chosen servings, converted
  // to the chosen unit system.
  const describeIngredient = (ingredient: RecipeIngredient) => {
    const { numPart, unitPart, remainder } = parseIngredientText(
      ingredient.original || ingredient.name
    );

    // One shared transform — the same one the steps and cooking mode use.
    const adjusted = adjustQuantity(numPart, unitPart, adjustment.options);
    return {
      displayNum: numPart ? adjusted.num : '',
      displayUnit: numPart ? adjusted.unit : '',
      remainder,
      changed: !!numPart && adjusted.changed,
      original: adjusted.original,
    };
  };

  // Render a single ingredient row (display-only, pantry-derived)
  const renderRow = (ingredient: RecipeIngredient, lufaIndex: number) => {
    const entry = statusById.get(ingredient.id);
    const status = entry?.status ?? 'buy';
    const match = entry?.match ?? null;
    // 'probably' reads as still-needed, deliberately. The grocery list keeps
    // those rows on the list until the user confirms, and a row that looks
    // settled here while sitting on the shopping list is the same disagreement
    // between the two screens that this whole layer removed.
    const isHave = status === 'have' || status === 'staple';
    const { displayNum, displayUnit, remainder, changed, original } =
      describeIngredient(ingredient);
    const isRevealed = revealedOriginal === ingredient.id;

    const hasSubs = findSubstitutions(remainder).found;
    const appliedSub = appliedSubs[remainder];
    const isActiveSub = activeSub === remainder;
    const isPending = pendingCorrection === ingredient.id;

    return (
      <IngredientRow
        key={ingredient.id}
        rowKey={ingredient.id}
        status={status}
        isHave={isHave}
        displayNum={displayNum}
        displayUnit={displayUnit}
        remainder={remainder}
        appliedSubName={appliedSub ? substituteName(appliedSub.name, i18n.language) : undefined}
        hasSubs={hasSubs}
        isActiveSub={isActiveSub}
        isPending={isPending}
        showLufa={lufaDealIndices.has(lufaIndex) && lufaEnabled}
        twoCol={twoCol}
        replaceLabel={
          isActiveSub
            ? t('recipeDetail.replacing', 'en cours')
            : t('recipeDetail.replaceRow', 'remplacer')
        }
        qtyChanged={changed}
        originalValue={original}
        originalLabel={t('recipeDetail.originallyValue', {
          defaultValue: "à l'origine : {{value}}",
          value: original,
        })}
        isRevealed={isRevealed}
        onPressQty={() =>
          changed
            ? setRevealedOriginal(prev => (prev === ingredient.id ? null : ingredient.id))
            : onAddIngredient?.(ingredient)
        }
        onPressRow={() => (hasSubs ? handleOpenSub(remainder) : onAddIngredient?.(ingredient))}
        onOpenSub={() => handleOpenSub(remainder)}
        onToggleCorrection={() => {
          setOutFollowUp(null);
          setPendingCorrection(prev => (prev === ingredient.id ? null : ingredient.id));
        }}
        correctionStrip={renderCorrectionStrip(
          ingredient,
          status,
          match,
          remainder,
          entry?.detail,
          entry?.askedAbout
        )}
      />
    );
  };

  /**
   * Every ingredient in the block gets a full row, pantry or not (#580).
   *
   * Pantry items used to collapse into a chip line here. On a 393px phone the
   * chips had no width bound and ran off-screen, and on a recipe where 15 of 16
   * ingredients were already in the pantry the list all but disappeared — the
   * reader lost both the names and the per-row green check. Density is now a
   * deliberate tap on the section header, never something the list does by itself.
   */
  const renderBlock = (
    block: RecipeIngredient[],
    lufaIndexFor: (ing: RecipeIngredient, displayIdx: number) => number,
    _blockKey = 'block',
  ): React.ReactNode => {
    const rows = block.map((ingredient, index) =>
      renderRow(ingredient, lufaIndexFor(ingredient, index))
    );

    return twoCol ? <View style={styles.grid}>{rows}</View> : <>{rows}</>;
  };

  const listContent = useMemo(() => {
    if (hasGroups) {
      const lufaIndexById = new Map<number, number>();
      ingredients.forEach((ing, idx) => lufaIndexById.set(ing.id, idx));
      const lookup = (ing: RecipeIngredient) => lufaIndexById.get(ing.id) ?? -1;

      const { ungrouped, groups: groupedBlocks } = groupIngredientsForDisplay(ingredients, groups!);

      const renderGroupSegment = (name: string, items: RecipeIngredient[], key: string) => (
        <View key={key}>
          <View style={styles.groupHeaderContainer}>
            <Text style={styles.groupHeader}>{name}</Text>
            <View style={styles.groupHeaderUnderline} />
          </View>
          {renderBlock(items, lookup, key)}
        </View>
      );

      const segments: React.ReactNode[] = [];
      if (ungrouped.length > 0) {
        segments.push(
          <View key="seg-ungrouped">{renderBlock(ungrouped, lookup, 'seg-ungrouped')}</View>
        );
      }
      groupedBlocks.forEach((g, idx) => {
        segments.push(renderGroupSegment(g.name, g.items, `seg-${idx}`));
      });
      return segments;
    }

    if (hasInlineHeaders) {
      const lufaIndexById = new Map<number, number>();
      ingredients.forEach((ing, idx) => lufaIndexById.set(ing.id, idx));
      const segments: React.ReactNode[] = [];
      let currentGroup: RecipeIngredient[] = [];
      let currentHeader: string | null = null;
      let headerIdx = 0;

      const flushGroup = (key: string) => {
        if (currentGroup.length === 0) return;
        segments.push(
          <View key={key}>
            {!!currentHeader && (
              <View style={styles.groupHeaderContainer}>
                <Text style={styles.groupHeader}>{currentHeader}</Text>
                <View style={styles.groupHeaderUnderline} />
              </View>
            )}
            {renderBlock(currentGroup, ing => lufaIndexById.get(ing.id) ?? -1, key)}
          </View>
        );
        currentGroup = [];
      };

      for (const ing of sortedIngredients) {
        if (isInlineHeader(ing)) {
          flushGroup(`auto-${headerIdx}`);
          headerIdx++;
          currentHeader = (ing.original || ing.name).trim();
        } else {
          currentGroup.push(ing);
        }
      }
      flushGroup(`auto-${headerIdx}-last`);
      return segments;
    }

    // Lufa deal indices are positional against the FULL ingredient list, so they
    // are looked up by id rather than by the row's own index, which restarts at
    // zero inside every section.
    const flatLufaIndexById = new Map<number, number>();
    sortedIngredients.forEach((ing, idx) => flatLufaIndexById.set(ing.id, idx));
    return renderBlock(
      sortedIngredients,
      ing => flatLufaIndexById.get(ing.id) ?? -1,
      'flat'
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasGroups, hasInlineHeaders, groups, ingredients, sortedIngredients, statusById, pendingCorrection, outFollowUp, adjustment.options, revealedOriginal, servings, baseServings, activeSub, appliedSubs, lufaDealIndices, lufaEnabled, twoCol, t, i18n.language, shelfName]);

  const { have, buy } = statusResult.coverage;
  const hasIngredients = ingredients.length > 0;

  // Two positions. There is no "original" button: the toggle simply *starts* on
  // the system the recipe was written in, so the as-written state is always one
  // tap away and nothing is marked until the user actually changes something.
  const isMetric = adjustment.system === 'metric';
  const unitToggle = (
    <View style={styles.unitToggle}>
      <TouchableOpacity
        style={[styles.unitOption, isMetric && styles.unitOptionActive]}
        onPress={() => adjustment.setSystem('metric')}
        activeOpacity={0.7}
      >
        <Text style={[styles.unitOptionText, isMetric && styles.unitOptionTextActive]}>
          {t('recipeDetail.metric')}
        </Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.unitOption, !isMetric && styles.unitOptionActive]}
        onPress={() => adjustment.setSystem('imperial')}
        activeOpacity={0.7}
      >
        <Text style={[styles.unitOptionText, !isMetric && styles.unitOptionTextActive]}>
          {t('recipeDetail.imperial')}
        </Text>
      </TouchableOpacity>
    </View>
  );

  const groceryButton = (fullWidth: boolean) => (
    <TouchableOpacity
      style={[styles.groceryButton, fullWidth && styles.groceryButtonFull]}
      onPress={handleAddBuyToGrocery}
      activeOpacity={0.85}
      testID="add-to-grocery"
    >
      <Feather name="shopping-cart" size={fullWidth ? 16 : 14} color="#fff" />
      <Text style={[styles.groceryButtonText, fullWidth && styles.groceryButtonTextFull]}>
        {t('recipeDetail.addBuyToGrocery', {
          defaultValue: "Ajouter les {{count}} à l'épicerie",
          count: buy,
        })}
      </Text>
    </TouchableOpacity>
  );

  return (
    <View style={styles.container}>
      {/* Section header — title + "N à acheter", which is all the coverage
          banner really needed to say. The header is also the collapse control:
          the title group and the chevron are two separate tap targets rather
          than one wrapping the whole row, because on wide layouts the row also
          carries the servings stepper and the unit toggle, and a Touchable
          nested in a Touchable fires both on press. */}
      <View style={[styles.header, { paddingHorizontal: gutter }]}>
        <TouchableOpacity
          style={styles.titleGroup}
          onPress={toggleCollapsed}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityState={{ expanded: !isCollapsed }}
          accessibilityLabel={t('recipeDetail.ingredients', 'Ingrédients')}
          // RNW's TouchableOpacity drops accessibilityState.expanded — verified
          // in the DOM, the rendered button carried only role and aria-label.
          // ARIA props pass through directly on react-native-web >= 0.19.
          {...(isWeb ? { 'aria-expanded': !isCollapsed } : {})}
        >
          <View style={styles.titleRow}>
            <Text style={[styles.sectionTitle, !isWide && styles.sectionTitleNarrow]}>
              {t('recipeDetail.ingredients', 'Ingrédients')}
            </Text>
            {hasIngredients && buy > 0 && (
              <View style={[styles.buyPill, !isWide && styles.buyPillSmall]}>
                <Text style={[styles.buyPillText, !isWide && styles.buyPillTextSmall]}>
                  {t('recipeDetail.buyPill', {
                    defaultValue: '{{count}} à acheter',
                    count: buy,
                  })}
                </Text>
              </View>
            )}
          </View>
          {/* The recipe's own yield, in its own words ("Mis pour six"). Only
              present when the source gave a worded yield rather than a bare
              number — the stepper already carries the number. It rides inside
              the collapse control rather than beside it, so it adds no second
              tap target to the header. */}
          {yieldLabel ? (
            <Text style={styles.yieldSubtitle} numberOfLines={1}>
              {yieldLabel}
            </Text>
          ) : null}
        </TouchableOpacity>
        <View style={styles.headerRight}>
          {isWide && !isCollapsed && (
            <View style={styles.headerActions}>
              {/* No yieldLabel: the worded yield is the subtitle under the
                  section title now, and printing "4 to 6 servings" twice in
                  one header reads as a rendering bug. */}
              {onServingsChange && (
                <ServingsStepper
                  servings={servings}
                  onChange={onServingsChange}
                  variant="slim"
                />
              )}
              {unitToggle}
            </View>
          )}
          <TouchableOpacity
            onPress={toggleCollapsed}
            activeOpacity={0.7}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            style={styles.collapseChevron}
            // The title group above is the labelled control; this is the same
            // action rendered twice, so it stays out of the a11y tree.
            importantForAccessibility="no"
            accessibilityElementsHidden
          >
            <Feather
              name={isCollapsed ? 'chevron-down' : 'chevron-up'}
              size={20}
              color={Paprika.textSecondary}
            />
          </TouchableOpacity>
        </View>
      </View>

      {/* Servings (full card on narrow layouts) */}
      {!isCollapsed && !isWide && onServingsChange && (
        <View style={[styles.servingsMobile, { marginHorizontal: gutter }]}>
          {/* Same reason as the slim variant above — the subtitle carries it. */}
          <ServingsStepper
            servings={servings}
            onChange={onServingsChange}
            variant="full"
          />
        </View>
      )}

      {/* Pantry line. On narrow layouts the unit toggle rides at the right of
          this existing row rather than claiming one of its own. */}
      {!isCollapsed && hasIngredients && (
        <View
          style={[
            styles.pantryLine,
            // Wide: text and grocery button sit together. Narrow: the unit
            // toggle is pushed to the far right of the same row.
            isWide ? styles.pantryLineWide : styles.pantryLineNarrow,
            { paddingHorizontal: gutter },
          ]}
        >
          <Text style={styles.pantryText}>
            {/* French counts 0 as « one », so restInPantry with nothing on the
                shelf read « L'autre est à votre garde-manger » (#805). */}
            {buy > 0 && have === 0
              ? t('recipeDetail.nothingInPantry', 'Rien de tout ça au garde-manger.')
              : buy > 0
              ? t('recipeDetail.restInPantry', {
                  defaultValue: 'Les {{count}} autres sont à votre garde-manger.',
                  count: have,
                })
              : t('recipeDetail.haveEverything', 'Vous avez tout pour cuisiner.')}
          </Text>
          {isWide && buy > 0 ? groceryButton(false) : null}
          {!isWide ? unitToggle : null}
        </View>
      )}

      {/* States the whole transform in one line, with one-tap undo. Its mere
          presence is the signal that this is not the recipe as published. */}
      {!isCollapsed && adjustment.isAdjusted && (
        <View style={[styles.adjustedRow, { paddingHorizontal: gutter }]}>
          <AdjustedChip
            adjustment={adjustment}
            baseServings={baseServings}
            servings={servings}
            onResetServings={onResetServings}
          />
        </View>
      )}

      {/* Ingredient list */}
      {!isCollapsed && (
        <View style={[styles.listContainer, { marginHorizontal: gutter }]}>{listContent}</View>
      )}

      {/* Narrow layouts carry the grocery action below the list, full width */}
      {!isCollapsed && !isWide && hasIngredients && buy > 0 && (
        <View style={[styles.groceryFullWrap, { paddingHorizontal: gutter }]}>
          {groceryButton(true)}
        </View>
      )}

      {/* Substitution sheet / panel */}
      <SubstitutionSheet
        visible={activeSub !== null}
        ingredientName={activeSub || ''}
        selectedSubName={activeSub ? appliedSubs[activeSub]?.name : undefined}
        ownedIds={ownedIds}
        onClose={() => setActiveSub(null)}
        onSelect={handleSelectSub}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingTop: PaprikaSpacing.lg,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: PaprikaSpacing.sm,
  },
  // A column now: title + buy pill on one line, the worded yield beneath. Still
  // the collapse control, so everything inside it shares that one tap target.
  titleGroup: {
    flexShrink: 1,
    gap: 1,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flexShrink: 1,
  },
  yieldSubtitle: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textMuted,
  },
  sectionTitle: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
  },
  sectionTitleNarrow: {
    fontSize: 21,
    lineHeight: 28,
  },
  buyPill: {
    paddingVertical: 4,
    paddingHorizontal: 11,
    borderRadius: 999,
    backgroundColor: Paprika.brandTint,
    borderWidth: 1,
    borderColor: Paprika.brandSoft,
  },
  buyPillSmall: {
    paddingVertical: 3,
    paddingHorizontal: 9,
  },
  buyPillText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 12,
    color: Paprika.accent,
  },
  buyPillTextSmall: {
    fontSize: 11,
  },
  pantryLine: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 14,
    paddingTop: 13,
    paddingBottom: 6,
  },
  pantryLineWide: {
    justifyContent: 'flex-start',
  },
  pantryLineNarrow: {
    justifyContent: 'space-between',
  },
  pantryText: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    lineHeight: 18,
    color: Paprika.textMuted,
    flexShrink: 1,
  },
  groceryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    height: 34,
    paddingHorizontal: 14,
    borderRadius: 10,
    backgroundColor: Paprika.accent,
  },
  groceryButtonFull: {
    height: 46,
    borderRadius: 13,
    alignSelf: 'stretch',
  },
  groceryButtonText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 12.5,
    color: '#fff',
  },
  groceryButtonTextFull: {
    fontSize: 14.5,
  },
  groceryFullWrap: {
    paddingTop: 12,
    paddingBottom: 4,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
    flexWrap: 'wrap',
  },
  // Collapse affordance: the chevron sits at the far right of the header row,
  // after the wide-layout actions. No background and no border at rest — the
  // chevron alone carries the affordance (DESIGN.md, No-Line Rule). Matches
  // AlreadyHaveSection.tsx and WeekListView.tsx.
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
    flexShrink: 0,
  },
  collapseChevron: {
    padding: 2,
  },
  unitToggle: {
    flexDirection: 'row',
    backgroundColor: Paprika.surfaceLow,
    borderRadius: PaprikaRadius.full,
    padding: 3,
  },
  unitOption: {
    paddingVertical: 6,
    paddingHorizontal: 16,
    borderRadius: PaprikaRadius.full,
  },
  unitOptionActive: {
    backgroundColor: Paprika.accent,
  },
  unitOptionText: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.textMuted,
    textTransform: 'capitalize',
  },
  unitOptionTextActive: {
    color: '#FFFFFF',
  },
  servingsMobile: {
    marginHorizontal: PaprikaSpacing.lg,
    marginBottom: PaprikaSpacing.md,
  },
  adjustedRow: {
    paddingTop: 4,
    paddingBottom: 8,
  },
  listContainer: {
    marginHorizontal: PaprikaSpacing.lg,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: PaprikaSpacing.xl,
  },
  rowGrid: {
    // Two columns; subtract half the columnGap so both fit per line.
    width: '46%',
    flexGrow: 1,
    maxWidth: '48%',
  },
  row: {
    flexDirection: 'row',
    // Top-aligned, not centred. A 3-line ingredient ("sushi-grade tuna
    // (yellowfin, ahi or bigeye) or salmon…") used to centre its mark and
    // quantity against the whole wrapped block, so cart icons and numbers in
    // the two-column grid drifted out of line with their neighbour. With the
    // quantity now at the right edge, top-alignment is also what keeps it on
    // the name's FIRST line instead of floating to the middle of the wrap.
    alignItems: 'flex-start',
    gap: PaprikaSpacing.sm,
    paddingVertical: 12,
    paddingHorizontal: PaprikaSpacing.sm,
    borderRadius: PaprikaRadius.sm,
    minHeight: 48,
  },
  rowActiveSub: {
    backgroundColor: Paprika.brandTint,
  },
  rowPending: {
    backgroundColor: Paprika.surfaceLow,
  },
  // Warm hairline, never rgba(0,0,0,x). Inset past the 24px mark + gap so the
  // rule starts under the ingredient name.
  rowDivider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: 'rgba(133, 116, 107, 0.16)',
    marginLeft: PaprikaSpacing.sm + 24 + PaprikaSpacing.sm,
    marginRight: PaprikaSpacing.sm,
  },
  qtyBlock: {
    // No fixed width. The 32/52px columns this replaces existed to align the
    // NAMES; the names are flush left now and it is the quantities that align,
    // which the right edge does on its own.
    flexShrink: 0,
  },
  qtyText: {
    ...PaprikaTypography.bodySmall,
    // Sizing stays on the token scale; only the line box is overridden, so the
    // quantity shares the name's baseline instead of riding above it.
    lineHeight: PaprikaTypography.titleMedium.lineHeight,
    color: Paprika.textSecondary,
    textAlign: 'right',
    fontVariant: ['tabular-nums'],
  },
  // The one signal that this number is not what the author wrote.
  qtyChanged: {
    color: Paprika.accent,
    ...(isWeb
      ? { textDecorationLine: 'underline', textDecorationStyle: 'dotted' as const }
      : {}),
  },
  originalText: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
    fontStyle: 'italic',
  },
  nameBlock: {
    flex: 1,
    gap: 1,
  },
  name: {
    ...PaprikaTypography.titleMedium,
  },
  nameBuy: {
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    color: Paprika.textPrimary,
  },
  nameHave: {
    fontFamily: 'Inter-Medium',
    fontWeight: '500',
    color: Paprika.textMuted,
  },
  appliedSubText: {
    ...PaprikaTypography.caption,
    color: Paprika.accent,
  },
  // The confirm block. Tinted, borderless (the No-Line Rule), inset to sit
  // under the name rather than the status mark.
  coverageStrip: {
    marginLeft: 36,
    marginRight: PaprikaSpacing.sm,
    marginBottom: PaprikaSpacing.sm,
    backgroundColor: PaprikaFreshness.review.bg,
    borderRadius: PaprikaRadius.md,
    paddingVertical: 12,
    paddingHorizontal: 14,
  },
  coverageQuestion: {
    ...PaprikaTypography.bodySmall,
    fontFamily: 'Inter-Medium',
    fontWeight: '500',
    color: PaprikaFreshness.review.text,
    marginBottom: 4,
  },
  coverageYield: {
    ...PaprikaTypography.caption,
    fontFamily: 'Inter-Regular',
    color: PaprikaFreshness.review.text,
    opacity: 0.75,
    marginBottom: 10,
  },
  coverageActions: {
    flexDirection: 'row',
    gap: PaprikaSpacing.sm,
    flexWrap: 'wrap',
  },
  // Terracotta, not the review purple. The tint carries "unsure"; a button is
  // chrome, and chrome gets the one strong accent (DESIGN.md).
  coverageYes: {
    borderRadius: PaprikaRadius.full,
    paddingVertical: 7,
    paddingHorizontal: 16,
    backgroundColor: Paprika.accent,
  },
  coverageYesText: {
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    fontSize: 11.5,
    color: '#fff',
  },
  coverageNo: {
    borderRadius: PaprikaRadius.full,
    paddingVertical: 7,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: Paprika.border,
  },
  coverageNoText: {
    fontFamily: 'Inter-Medium',
    fontWeight: '500',
    fontSize: 11.5,
    color: Paprika.textSecondary,
  },
  correctionStrip: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: PaprikaSpacing.sm,
    marginLeft: 40,
    marginRight: PaprikaSpacing.sm,
    marginBottom: PaprikaSpacing.sm,
    backgroundColor: Paprika.cardBg,
    borderWidth: 1,
    borderColor: Paprika.borderLight,
    borderRadius: PaprikaRadius.md,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  correctionQuestion: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
    flex: 1,
    minWidth: 120,
  },
  correctionAction: {
    borderRadius: PaprikaRadius.full,
    paddingVertical: 7,
    paddingHorizontal: 12,
  },
  correctionActionText: {
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    fontSize: 11.5,
    color: '#fff',
  },
  correctionCancel: {
    borderRadius: PaprikaRadius.full,
    paddingVertical: 7,
    paddingHorizontal: 12,
    backgroundColor: Paprika.surfaceLow,
  },
  correctionCancelText: {
    fontFamily: 'Inter-Medium',
    fontWeight: '500',
    fontSize: 11.5,
    color: Paprika.textMuted,
  },
  replaceButton: {
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    // Same line box as the name and the quantity, so all three sit on one row.
    height: PaprikaTypography.titleMedium.lineHeight,
  },
  lufaBadge: {
    backgroundColor: '#E8F5E9',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  lufaBadgeText: {
    fontSize: 10,
    color: '#2E7D32',
    fontWeight: '600',
  },
  groupHeaderContainer: {
    marginTop: PaprikaSpacing.md,
    marginBottom: PaprikaSpacing.xs,
  },
  groupHeader: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    paddingBottom: PaprikaSpacing.xs,
  },
  groupHeaderUnderline: {
    width: 40,
    height: 2,
    backgroundColor: Paprika.accent,
    borderRadius: 1,
  },
});

export default RecipeIngredients;
